import { z } from 'zod';
import { INCIDENT_CASE_STATUSES, INCIDENT_CLASSIFICATIONS, INCIDENT_CYCLE_KINDS, INCIDENT_SLA_FILTERS, INCIDENT_TYPES, INCIDENT_URGENCIES, } from './incident-workflow.js';
import { INCIDENT_LIST_SCOPES } from './incident-dto.js';
import { isValidTimeZone } from './report-dto.js';
import { smartReportDateSchema } from './smart-report.js';
/** Which timestamp the date range applies to. */
export const INCIDENT_REPORT_DATE_BASES = ['occurred', 'reported', 'submitted', 'closed'];
export const INCIDENT_REPORT_PREVIEW_MAX_PAGE_SIZE = 100;
export const INCIDENT_REPORT_EXPORT_MAX_ROWS = 20_000;
export const INCIDENT_REPORT_PDF_MAX_ROWS = 2_000;
/**
 * Allowlist. The MPI ID / patient name and every free-text narrative field
 * (description, impact, corrections, 5-whys, RCA, CAPA text) are deliberately
 * NOT available: reports leave the incident's own access controls.
 */
export const INCIDENT_REPORT_COLUMNS = [
    { key: 'number', label: 'Incident' },
    { key: 'caseStatus', label: 'Status' },
    { key: 'unit', label: 'Unit' },
    { key: 'floor', label: 'Floor' },
    { key: 'occurredAt', label: 'Occurred' },
    { key: 'reportedAt', label: 'Reported' },
    { key: 'submittedAt', label: 'Submitted' },
    { key: 'raisedBy', label: 'Raised by' },
    { key: 'classification', label: 'Classification' },
    { key: 'incidentType', label: 'Incident type' },
    { key: 'urgency', label: 'Urgency' },
    { key: 'workgroup', label: 'Workgroup' },
    { key: 'stakeholders', label: 'Stakeholders' },
    { key: 'cycleKind', label: 'Cycle' },
    { key: 'reopenCount', label: 'Reopened (count)' },
    { key: 'escalationLevel', label: 'Escalation level' },
    { key: 'responseDueAt', label: 'Response due' },
    { key: 'resolutionDueAt', label: 'Resolution due' },
    { key: 'closedAt', label: 'Closed' },
    { key: 'capaStatus', label: 'CAPA status' },
];
const COLUMN_KEYS = INCIDENT_REPORT_COLUMNS.map((c) => c.key);
export const INCIDENT_REPORT_DEFAULT_COLUMNS = [
    'number', 'caseStatus', 'unit', 'occurredAt', 'reportedAt', 'classification', 'incidentType', 'urgency', 'workgroup', 'closedAt',
];
const idList = z.array(z.string().uuid()).max(100);
export const incidentReportQuerySchema = z
    .object({
    date: smartReportDateSchema,
    dateBasis: z.enum(INCIDENT_REPORT_DATE_BASES).default('occurred'),
    /** Which of the actor's read scopes to report on; the server refuses one they do not hold. */
    scope: z.enum(INCIDENT_LIST_SCOPES).default('all'),
    caseStatuses: z.array(z.enum(INCIDENT_CASE_STATUSES)).max(10).optional(),
    classifications: z.array(z.enum(INCIDENT_CLASSIFICATIONS)).max(5).optional(),
    incidentTypes: z.array(z.enum(INCIDENT_TYPES)).max(10).optional(),
    urgencies: z.array(z.enum(INCIDENT_URGENCIES)).max(5).optional(),
    cycleKinds: z.array(z.enum(INCIDENT_CYCLE_KINDS)).max(5).optional(),
    slaStates: z.array(z.enum(INCIDENT_SLA_FILTERS)).max(5).optional(),
    workgroupIds: idList.optional(),
    officeLocationIds: idList.optional(),
    floorIds: idList.optional(),
    q: z.string().trim().min(1).max(50).optional(),
    columns: z.array(z.enum(COLUMN_KEYS)).min(1).max(COLUMN_KEYS.length).refine((c) => new Set(c).size === c.length, 'duplicate columns').default([...INCIDENT_REPORT_DEFAULT_COLUMNS]),
    page: z.number().int().min(1).max(100).default(1),
    pageSize: z.number().int().min(1).max(INCIDENT_REPORT_PREVIEW_MAX_PAGE_SIZE).default(25),
    tz: z.string().trim().min(1).max(64).refine(isValidTimeZone, { message: 'tz must be an IANA time zone' }).optional(),
})
    .strict();
//# sourceMappingURL=smart-report-incident.js.map